import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import BrandLogo from '../components/BrandLogo';

export default function Support() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: user?.email || '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Raising a ticket requires an account (enforced server-side). Anonymous
  // visitors are pointed at login/signup, or to Book a Demo which stays public.
  if (!user) {
    return (
      <div className="auth-page" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="auth-card" style={{ textAlign: 'center', maxWidth: 460 }}>
          <BrandLogo />
          <div style={{ fontSize: 48, margin: '32px 0 16px' }}>🔒</div>
          <h2 style={{ marginBottom: 8 }}>Sign in to get support</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>
            Support tickets are tied to your account so we can track and respond to them.
            Please log in or create an account to raise a ticket.
          </p>
          <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
            <Link to="/login" className="btn btn-primary btn-block">Log In</Link>
            <Link to="/signup" className="btn btn-secondary btn-block">Create Account</Link>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '8px 0 0' }}>
              Not a customer yet? <Link to="/book-demo">Book a demo</Link> instead.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || form.name.trim().length < 2) {
      return setError('Please enter your name');
    }
    if (!form.email.trim()) {
      return setError('Please enter your email address');
    }
    if (!form.subject.trim() || form.subject.trim().length < 3) {
      return setError('Please enter a subject (at least 3 characters)');
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      return setError('Please provide more details (at least 10 characters)');
    }

    setLoading(true);
    try {
      const data = await api.createSupportTicket(form);
      setSuccess(true);
      setTicketId(data.ticketId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="auth-card" style={{ textAlign: 'center', maxWidth: 480 }}>
          <BrandLogo />
          <div style={{ fontSize: 48, margin: '32px 0 16px' }}>✅</div>
          <h2 style={{ marginBottom: 8 }}>Ticket Created!</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
            Your support ticket <strong>#{ticketId}</strong> has been created. We'll respond within 24 hours.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
            A confirmation email has been sent to <strong>{form.email}</strong>.
          </p>
          <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
            <button
              className="btn btn-primary btn-block"
              onClick={() => { setSuccess(false); setForm({ name: '', email: user?.email || '', phone: '', subject: '', message: '' }); }}
            >
              Create Another Ticket
            </button>
            <Link to={user ? '/dashboard' : '/'} className="btn btn-secondary btn-block">
              {user ? 'Go to Dashboard' : 'Back to Home'}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div style={{ width: '100%', maxWidth: 900, display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap', padding: 20 }}>
        {/* Left - Info */}
        <div style={{ flex: '1 1 320px', color: '#fff', padding: '40px 0' }}>
          <Link to="/" style={{ color: '#fff', textDecoration: 'none', fontSize: 24, fontWeight: 700, display: 'block', marginBottom: 24 }}>
            LoopVisitor
          </Link>
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 16, lineHeight: 1.2 }}>
            How can we help?
          </h1>
          <p style={{ fontSize: 16, opacity: 0.9, marginBottom: 32, lineHeight: 1.6 }}>
            Our support team is here to help you get the most out of LoopVisitor.
            Submit a ticket and we'll get back to you quickly.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: '⚡', text: 'Average response time: under 24 hours' },
              { icon: '🛡️', text: 'Dedicated support for all plans' },
              { icon: '📧', text: 'Email confirmation for every ticket' },
              { icon: '🔄', text: 'Track ticket status in your dashboard' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, opacity: 0.9 }}>
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right - Form */}
        <div className="auth-card" style={{ flex: '1 1 380px', maxWidth: 460 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Raise a Support Ticket</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>
            Describe your issue and we'll get back to you within 24 hours.
          </p>

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Your Name *</label>
              <input
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="john@business.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Phone <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="+1 555 123 4567"
              />
            </div>
            <div className="form-group">
              <label>Subject *</label>
              <input
                value={form.subject}
                onChange={(e) => update('subject', e.target.value)}
                placeholder="Brief description of your issue"
                required
              />
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea
                value={form.message}
                onChange={(e) => update('message', e.target.value)}
                placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, or relevant information..."
                rows={8}
                required
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)', resize: 'vertical', minHeight: 180 }}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Submit Ticket'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
